import {stage} from './stage';

/**
 * Auth broker endpoints, per stage (FNP-871).
 *
 * The broker mints single-use authorization codes for tokens the PORTAL has
 * already obtained. The portal remains the authenticator in step 1 of the
 * FNP-866 architecture; the broker only replaces the way tokens are handed to
 * a satellite app — a code redeemed over POST, instead of raw JWTs in a URL
 * fragment.
 *
 * ⚠️ GRADUAL MIGRATION. Not every app moves at once. An app opts in simply by
 * asking for `response_type=code`; everything else keeps the fragment relay.
 * See AuthorizePage.tsx for where that fork is made.
 *
 * An EMPTY string here means "no broker in this stage" — the portal then
 * refuses a code-flow request rather than silently falling back to the
 * fragment, because a silent downgrade would defeat the point of migrating.
 */
const authBroker: Record<'prod' | 'test' | 'dev' | 'ide', string> = {
    // Stack output `AuthBrokerUrl` of flexinet-auth-broker-api-<stage>, without
    // the trailing slash: callers append `/oauth2/...`. API Gateway default
    // URLs; there is no custom domain yet (FNP-866 step 2).
    prod: 'https://aty3ojbu1h.execute-api.us-west-2.amazonaws.com/prod',
    test: 'https://zk71rojmk1.execute-api.us-west-2.amazonaws.com/prod',
    dev: 'https://ft8v2ll4uc.execute-api.us-west-2.amazonaws.com/prod',
    // Local development points at the same DEV broker — there is no local
    // broker to run, and the dev broker's allowlist already carries
    // https://localhost:8013.
    ide: 'https://ft8v2ll4uc.execute-api.us-west-2.amazonaws.com/prod',
};

export const authBrokerEndpoint = authBroker[stage];

export const isAuthBrokerAvailable = (): boolean => authBrokerEndpoint !== '';

/**
 * The broker endpoint the PORTAL redirects back to after authenticating.
 *
 * Under the code flow the broker sends the user here with this as its
 * `redirect_uri`; the portal must therefore accept it as a valid redirect
 * target (see isAllowedRedirectUrl). Derived from the configured endpoint so
 * there is exactly one source of truth and no caller-supplied component.
 */
export const authBrokerCallbackUrl = authBrokerEndpoint === ''
    ? ''
    : `${authBrokerEndpoint}/oauth2/callback`;
